import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Save, Loader2, Eye, EyeOff, Send } from "lucide-react";
import { useSettings } from "../../lib/useSettings";
import { toast } from "sonner";
import ImageUploader from "./ImageUploader";

export default function SettingsAdmin() {
  const { settings, refresh } = useSettings();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showSmtpPwd, setShowSmtpPwd] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);

  useEffect(() => { if (settings) setS({ ...settings, hours: settings.hours || [], social: settings.social || {} }); }, [settings]);

  if (!s) return <div className="p-10">Chargement...</div>;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", s);
      await refresh();
      toast.success("Paramètres enregistrés");
    } catch { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-6 md:p-10 max-w-4xl" data-testid="admin-settings-page">
      <header className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-semibold mb-1">Configuration</div>
        <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">Paramètres du site</h1>
      </header>

      <Card className="p-6 border mb-4 space-y-4">
        <h3 className="font-display font-bold">Identité</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>Nom de l'entreprise</Label><Input value={s.company_name||""} onChange={e=>setS({...s, company_name: e.target.value})}/></div>
          <div><Label>Secteur</Label><Input value={s.sector||""} onChange={e=>setS({...s, sector: e.target.value})}/></div>
        </div>
        <div><Label>Tagline</Label><Input value={s.tagline||""} onChange={e=>setS({...s, tagline: e.target.value})}/></div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label>Texte de présentation (pied de page)</Label>
            <span className="text-[10px] text-[hsl(var(--muted-foreground))] tabular-nums">{(s.footer_description||"").length} caractères</span>
          </div>
          <Textarea rows={6} value={s.footer_description||""} onChange={e=>setS({...s, footer_description: e.target.value})} placeholder="Texte puissant affiché sous le logo du footer (10 ans d'expérience, marques installées, zone d'intervention, engagements…)" data-testid="settings-footer-description"/>
          <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">Idéal : 300–500 caractères. Les retours à la ligne sont conservés. Ce texte remplace la tagline dans le footer.</p>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label>Logo de l'entreprise</Label>
            <ImageUploader folder="logos" label="Uploader un logo" onUploaded={(url) => setS({ ...s, logo_url: url })} />
          </div>
          <Input value={s.logo_url||""} onChange={e=>setS({...s, logo_url: e.target.value})} placeholder="URL ou /uploads/cms/logos/..."/>
          {s.logo_url && (
            <div className="mt-2 p-3 rounded-xl bg-[hsl(var(--muted))]/40 border border-[hsl(var(--border))] inline-block">
              <img src={s.logo_url} alt="Logo" className="h-16 w-auto"/>
            </div>
          )}
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          <div><Label>Couleur principale</Label><Input type="color" value={s.primary_color||"#1E3A5F"} onChange={e=>setS({...s, primary_color: e.target.value})} className="h-11"/></div>
          <div><Label>Couleur secondaire</Label><Input type="color" value={s.secondary_color||"#3DB9E8"} onChange={e=>setS({...s, secondary_color: e.target.value})} className="h-11"/></div>
          <div><Label>Couleur accent</Label><Input type="color" value={s.accent_color||"#E30613"} onChange={e=>setS({...s, accent_color: e.target.value})} className="h-11"/></div>
        </div>
      </Card>

      <Card className="p-6 border mb-4 space-y-4">
        <h3 className="font-display font-bold">Hero (page d'accueil)</h3>
        <div><Label>Titre H1</Label><Input value={s.hero_title||""} onChange={e=>setS({...s, hero_title: e.target.value})}/></div>
        <div><Label>Sous-titre</Label><Textarea rows={2} value={s.hero_subtitle||""} onChange={e=>setS({...s, hero_subtitle: e.target.value})}/></div>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>Type arrière-plan</Label>
            <Select value={s.hero_bg_type||"image"} onValueChange={v=>setS({...s, hero_bg_type: v})}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent><SelectItem value="image">Image</SelectItem><SelectItem value="carousel">Carrousel</SelectItem><SelectItem value="youtube">Vidéo YouTube</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label>YouTube ID (arrière-plan hero si vidéo)</Label><Input value={s.hero_youtube||""} onChange={e=>setS({...s, hero_youtube: e.target.value})}/></div>
        </div>
        <div><Label>Vidéo de présentation (bulle ronde dans hero) — YouTube ID</Label><Input value={s.presentation_video_youtube||""} onChange={e=>setS({...s, presentation_video_youtube: e.target.value})} placeholder="ex: dQw4w9WgXcQ"/></div>
        <div><Label>Images hero (une URL par ligne)</Label><Textarea rows={3} value={(s.hero_bg_images||[]).join("\n")} onChange={e=>setS({...s, hero_bg_images: e.target.value.split("\n").filter(Boolean)})}/></div>
      </Card>

      <Card className="p-6 border mb-4 space-y-4">
        <h3 className="font-display font-bold">Avis Google</h3>
        <div className="grid md:grid-cols-3 gap-3">
          <div><Label>Nombre d'avis</Label><Input type="number" value={s.reviews_count||127} onChange={e=>setS({...s, reviews_count: +e.target.value})}/></div>
          <div><Label>Note moyenne</Label><Input type="number" step="0.1" value={s.reviews_average||4.9} onChange={e=>setS({...s, reviews_average: +e.target.value})}/></div>
          <div><Label>Depuis (année)</Label><Input type="number" value={s.reviews_since_year||2022} onChange={e=>setS({...s, reviews_since_year: +e.target.value})}/></div>
        </div>
        <div><Label>Lien Google "Laisser un avis"</Label>
          <Input
            value={s.google_review_url||""}
            onChange={e=>setS({...s, google_review_url: e.target.value})}
            placeholder="https://g.page/r/XXXXXXXXX/review"
          />
          {(() => {
            const v = (s.google_review_url||"").trim();
            if (!v) {
              if (s.google_place_id) {
                return <p className="text-[11px] text-[color:var(--brand-sky)] mt-1 font-semibold">ℹ Champ vide : le bouton "Laisser un avis" utilisera automatiquement votre <code className="font-mono">place_id</code> Google ({s.google_place_id.slice(0,12)}…) — ce qui fonctionne très bien. Vous pouvez toutefois coller ici le <strong>lien court</strong> officiel (format <code className="font-mono">g.page/r/.../review</code>) si vous le possédez.</p>;
              }
              return <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">Collez le lien court fourni par Google Business Profile (format <code className="font-mono">g.page/r/.../review</code>).</p>;
            }
            const ok = /^https?:\/\/g\.page\/r\/[A-Za-z0-9_-]+\/review\/?$/i.test(v)
              || /^https?:\/\/search\.google\.com\/local\/writereview\?/i.test(v);
            const warnShort = /^https?:\/\/g\.page\/r\/[A-Za-z0-9_-]+\/?$/i.test(v);
            if (ok) return <p className="text-[11px] text-emerald-600 mt-1 font-semibold">✓ Format valide — le lien ouvrira directement le formulaire d'avis.</p>;
            if (warnShort) return <p className="text-[11px] text-amber-600 mt-1 font-semibold">⚠ Ajoutez <code className="font-mono">/review</code> à la fin du lien (sinon Google redirige vers la fiche seulement).</p>;
            return <p className="text-[11px] text-amber-600 mt-1 font-semibold">⚠ Format non reconnu. Utilisez le lien court de Google Business Profile : <code className="font-mono">g.page/r/XXXXXX/review</code>.</p>;
          })()}
        </div>
        <div>
          <Label>Témoignages réels (un par ligne, format : nom | ville | texte | date)</Label>
          <Textarea
            rows={6}
            placeholder="Sophie M. | Salon-de-Provence | Travail impeccable, équipe au top | il y a 2 mois"
            value={(s.testimonials||[]).map(t => `${t.name||""} | ${t.city||""} | ${t.text||""} | ${t.date||""}`).join("\n")}
            onChange={e => {
              const items = e.target.value.split("\n").filter(Boolean).map(line => {
                const [name, city, text, date] = line.split("|").map(x=>x.trim());
                return { name: name||"", city: city||"", text: text||"", date: date||"", rating: 5 };
              });
              setS({...s, testimonials: items});
            }}
          />
          <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">Vide par défaut. Ajoutez vos vrais avis Google ici (copier-coller).</p>
        </div>
      </Card>

      <Card className="p-6 border mb-4 space-y-4">
        <h3 className="font-display font-bold">Contact & adresse</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>Téléphone</Label><Input value={s.phone||""} onChange={e=>setS({...s, phone: e.target.value})}/></div>
          <div><Label>Email</Label><Input value={s.email||""} onChange={e=>setS({...s, email: e.target.value})}/></div>
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          <div><Label>Adresse publique</Label><Input value={s.address_public||""} onChange={e=>setS({...s, address_public: e.target.value})}/></div>
          <div><Label>Siège social</Label><Input value={s.address_headquarters||""} onChange={e=>setS({...s, address_headquarters: e.target.value})}/></div>
          <div><Label>RDV</Label><Input value={s.address_appointment||""} onChange={e=>setS({...s, address_appointment: e.target.value})}/></div>
        </div>
      </Card>

      <Card className="p-6 border mb-4 space-y-3">
        <h3 className="font-display font-bold">Horaires</h3>
        {(s.hours||[]).map((h,i)=>(
          <div key={i} className="grid grid-cols-[100px_1fr_1fr_auto] gap-2 items-center">
            <span className="text-sm font-semibold">{h.day}</span>
            <Input value={h.morning||""} onChange={e=>{const hours=[...s.hours]; hours[i]={...h, morning: e.target.value}; setS({...s, hours});}} placeholder="08:00-12:00"/>
            <Input value={h.afternoon||""} onChange={e=>{const hours=[...s.hours]; hours[i]={...h, afternoon: e.target.value}; setS({...s, hours});}} placeholder="14:00-18:00"/>
            <div className="flex items-center gap-2"><Switch checked={!h.closed} onCheckedChange={v=>{const hours=[...s.hours]; hours[i]={...h, closed: !v}; setS({...s, hours});}}/><span className="text-xs">{h.closed?"Fermé":"Ouvert"}</span></div>
          </div>
        ))}
      </Card>

      <Card className="p-6 border mb-4 space-y-3">
        <h3 className="font-display font-bold">Réseaux sociaux</h3>
        {["facebook","instagram","linkedin","youtube"].map(k => (
          <div key={k}><Label className="capitalize">{k}</Label><Input value={s.social?.[k]||""} onChange={e=>setS({...s, social:{...s.social, [k]: e.target.value}})} placeholder={`URL ${k}`}/></div>
        ))}
      </Card>

      <Card className="p-6 border mb-4 space-y-4">
        <h3 className="font-display font-bold">SMTP — Envoi des leads par email</h3>
        <p className="text-xs text-[hsl(var(--muted-foreground))] -mt-2">Configurez votre serveur SMTP (PlanetHoster, OVH, Gmail, etc.) pour que chaque demande de devis/rappel soit envoyée par email à votre agence.</p>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>Serveur SMTP</Label><Input value={s.smtp_host||""} onChange={e=>setS({...s, smtp_host: e.target.value})} placeholder="mail.kap-clim.fr"/></div>
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <div><Label>Port</Label><Input type="number" value={s.smtp_port||465} onChange={e=>setS({...s, smtp_port: +e.target.value})}/></div>
            <div><Label>SSL</Label><div className="flex items-center h-10 gap-2"><Switch checked={s.smtp_use_ssl !== false} onCheckedChange={v=>setS({...s, smtp_use_ssl: v})}/></div></div>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>Identifiant</Label><Input value={s.smtp_user||""} onChange={e=>setS({...s, smtp_user: e.target.value})} placeholder="noreply@kap-clim.fr"/></div>
          <div>
            <Label>Mot de passe</Label>
            <div className="relative mt-1.5">
              <Input
                type={showSmtpPwd ? "text" : "password"}
                value={s.smtp_password === "__set__" ? "" : (s.smtp_password || "")}
                onChange={e => setS({...s, smtp_password: e.target.value})}
                placeholder={s.smtp_password === "__set__" ? "•••••••• (déjà enregistré — laissez vide pour le conserver)" : "••••••••"}
                autoComplete="new-password"
                className="pr-10"
                data-testid="smtp-password-input"
              />
              <button
                type="button"
                onClick={() => setShowSmtpPwd(v => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 inline-flex items-center justify-center rounded-md text-[hsl(var(--muted-foreground))] hover:text-[color:var(--brand-navy)] hover:bg-[hsl(var(--muted))] transition-colors"
                aria-label={showSmtpPwd ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                data-testid="smtp-password-toggle"
              >
                {showSmtpPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>Nom expéditeur</Label><Input value={s.smtp_from_name||""} onChange={e=>setS({...s, smtp_from_name: e.target.value})} placeholder="Kloub'Air Plomberie"/></div>
          <div><Label>Email expéditeur (From)</Label><Input value={s.smtp_from_email||""} onChange={e=>setS({...s, smtp_from_email: e.target.value})} placeholder="noreply@kap-clim.fr"/></div>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>Répondre à (Reply-To)</Label><Input value={s.smtp_reply_to||""} onChange={e=>setS({...s, smtp_reply_to: e.target.value})} placeholder="contact@kap-clim.fr"/></div>
          <div><Label>Destinataire des notifications</Label><Input value={s.notification_email||""} onChange={e=>setS({...s, notification_email: e.target.value})} placeholder="contact@kap-clim.fr"/></div>
        </div>

        {/* Test SMTP block */}
        <div className="bg-slate-50 border rounded-xl p-4 mt-2 space-y-3" data-testid="smtp-test-block">
          <div>
            <Label>Tester l'envoi vers (laissez vide pour utiliser le destinataire des notifications)</Label>
            <Input
              type="email"
              value={testTo}
              onChange={e => setTestTo(e.target.value)}
              placeholder={s.notification_email || "votre@email.fr"}
              className="mt-1.5"
              data-testid="smtp-test-recipient"
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button type="button" variant="outline" disabled={testing} onClick={async () => {
              setTesting(true);
              try {
                const payload = testTo.trim() ? { to: testTo.trim() } : {};
                const r = await (await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/test-smtp`, {
                  method: "POST",
                  headers: {"Content-Type":"application/json","Authorization":`Bearer ${localStorage.getItem("kap_token")}`},
                  body: JSON.stringify(payload),
                })).json();
                if (r.sent) {
                  toast.success("Email de test envoyé — vérifiez " + (testTo || s.notification_email || "votre boîte"));
                } else {
                  const reasons = { smtp_not_configured: "Champs manquants (hôte/identifiant/mot de passe/expéditeur/destinataire)" };
                  const detail = r.error || reasons[r.reason] || r.reason || "inconnu";
                  const type = r.error_type ? ` (${r.error_type})` : "";
                  const hint = r.hint ? `\n💡 ${r.hint}` : "";
                  toast.error("Échec SMTP : " + detail + type + hint, { duration: 14000 });
                  console.error("SMTP test failure", r);
                }
              } catch (e) { toast.error("Erreur : " + e.message); }
              finally { setTesting(false); }
            }} data-testid="test-smtp-btn">
              {testing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />}
              Envoyer un email de test
            </Button>
            <a href="/admin/emails" className="text-xs font-semibold text-[color:var(--brand-sky)] hover:underline" data-testid="link-email-history">
              Voir l'historique des emails →
            </a>
            <span className="text-xs text-[hsl(var(--muted-foreground))]">Enregistrez avant de tester.</span>
          </div>
        </div>
      </Card>

      {/* Support widget */}
      <Card className="p-5 space-y-3" data-testid="settings-support-widget">
        <h2 className="font-display font-bold text-[color:var(--brand-navy)]">Widget de support (chat live / helpdesk)</h2>
        <p className="text-xs text-[hsl(var(--muted-foreground))] -mt-2">
          Collez ici le code HTML/JavaScript fourni par votre outil tiers (Crisp, Tawk.to, Intercom, Freshdesk, Zendesk, LiveChat, HelpScout, Brevo Conversations…).
          Il s'affichera automatiquement sur l'onglet <strong>Support</strong> du back-office et peut aussi être activé sur le site public.
        </p>
        <div>
          <Label>Widget HTML / Script</Label>
          <Textarea
            rows={8}
            value={s.support_widget_html || ""}
            onChange={(e) => setS({ ...s, support_widget_html: e.target.value })}
            className="font-mono text-xs"
            placeholder='<!-- ex. Tawk.to -->\n<script type="text/javascript">\nvar Tawk_API=Tawk_API||{};\n(function(){var s1=document.createElement("script")...})();\n</script>'
            data-testid="settings-widget-html"
          />
          <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1.5">
            ⚠️ Le script sera exécuté dans un <code>iframe</code> isolé pour votre sécurité. Seuls des outils reconnus doivent être collés ici.
          </p>
        </div>
      </Card>

      <div className="sticky bottom-0 py-4 bg-gradient-to-t from-[hsl(var(--background))] to-transparent">
        <Button onClick={save} disabled={saving} className="bg-[color:var(--brand-navy)] hover:bg-[color:var(--brand-navy)]/90 w-full md:w-auto" data-testid="save-settings">{saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Save className="w-4 h-4 mr-1"/>Enregistrer tous les paramètres</>}</Button>
      </div>
    </div>
  );
}
